import {
  Tenant,
  buchananCommodityOptions,
  buchananOrderTypes,
  buchananReferenceNumberOptions,
  buchananRevenueCodes,
  buchananTransportTypeOptions,
  fetchCommodityOptions,
  fetchFreightTransportTypeOptions,
  fetchOrderTypes,
  fetchReferenceNumberOptions,
  fetchRevenueCodes,
  jrcCommodityOptions,
  jrcOrderTypes,
  jrcReferenceNumberOptions,
  jrcRevenueCodes,
  jrcTransportTypeOptions,
  larkinCommodityOptions,
  larkinOrderTypes,
  larkinRevenueCodes,
  larkinTransportTypeOptions,
  ptCommodityOptions,
  ptExtraReferenceNumberOptions,
  ptOrderTypes,
  ptRevenueCodes,
  ptTransportTypeOptions,
  rlsCommodityOptions,
  rlsOrderTypes,
  rlsRevenueCodes,
  rlsTransportTypeOptions,
  syfanCommodityOptions,
  syfanOrderTypes,
  syfanReferenceNumberOptions,
  syfanRevenueCodes,
  syfanTransportTypeOptions,
  tenantHostAliases,
  tridentCommodityOptions,
  tridentOrderTypes,
  tridentReferenceNumberOptions,
  tridentRevenueCodes,
  tridentTransportTypeOptions,
  tumaloCommodityOptions,
  tumaloOrderTypes,
  tumaloReferenceNumberOptions,
  tumaloRevenueCodes,
  tumaloTransportTypeOptions,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/constants';
import {
  McleodCommodityOption,
  McleodReferenceNumberOption,
  ValueLabelOption,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/types';
import { IntegrationCore } from 'types/Integration';
import { MaybeUndef, Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

export type SelectOption = {
  value: string;
  label: string;
};

export { getMcleodIntegrationFromList } from 'pages/QuoteView/LoadBuilding/utils/tmsIntegrations';

/**
 * Determines the tenant from a URL or string by checking if it contains any tenant identifier
 * (same idea as BE: strings.Contains on the tenant host).
 * @param input - The URL or string to check (e.g., "tms.syfanlogistics.com:5690")
 * @returns The matching Tenant enum value, or undefined if no match found
 */
export const getTenantFromUrl = (input: string): Undef<Tenant> => {
  const normalizedInput = input.toLowerCase();

  for (const tenant of Object.values(Tenant)) {
    if (normalizedInput.includes(tenant)) {
      return tenant;
    }
  }
  for (const [alias, tenant] of Object.entries(tenantHostAliases)) {
    if (normalizedInput.includes(alias)) {
      return tenant;
    }
  }

  captureException(
    `Unable to parse tenant enum from URL ${input}, unable to load tenant-specific enums`,
    {
      tenant: input,
    }
  );

  return undefined;
};

export const tenantToTransportTypeOptions: Record<Tenant, ValueLabelOption[]> =
  {
    [Tenant.Trident]: tridentTransportTypeOptions,
    [Tenant.Fetch]: fetchFreightTransportTypeOptions,
    [Tenant.Syfan]: syfanTransportTypeOptions,
    [Tenant.Tumalo]: tumaloTransportTypeOptions,
    [Tenant.RLS]: rlsTransportTypeOptions,
    [Tenant.Buchanan]: buchananTransportTypeOptions,
    [Tenant.Larkin]: larkinTransportTypeOptions,
    [Tenant.JRC]: jrcTransportTypeOptions,
    [Tenant.PT]: ptTransportTypeOptions,
  };

export const tenantToCommodityOptions: Record<Tenant, McleodCommodityOption[]> =
  {
    [Tenant.Trident]: tridentCommodityOptions,
    [Tenant.Fetch]: fetchCommodityOptions,
    [Tenant.Syfan]: syfanCommodityOptions,
    [Tenant.Tumalo]: tumaloCommodityOptions,
    [Tenant.RLS]: rlsCommodityOptions,
    [Tenant.Buchanan]: buchananCommodityOptions,
    [Tenant.Larkin]: larkinCommodityOptions,
    [Tenant.JRC]: jrcCommodityOptions,
    [Tenant.PT]: ptCommodityOptions,
  };

export const tenantToRevenueCodes: Record<Tenant, ValueLabelOption[]> = {
  [Tenant.Trident]: tridentRevenueCodes,
  [Tenant.Fetch]: fetchRevenueCodes,
  [Tenant.Syfan]: syfanRevenueCodes,
  [Tenant.Tumalo]: tumaloRevenueCodes,
  [Tenant.RLS]: rlsRevenueCodes,
  [Tenant.Buchanan]: buchananRevenueCodes,
  [Tenant.Larkin]: larkinRevenueCodes,
  [Tenant.JRC]: jrcRevenueCodes,
  [Tenant.PT]: ptRevenueCodes,
};

export const tenantToReferenceNumberOptions: Record<
  Tenant,
  McleodReferenceNumberOption[]
> = {
  [Tenant.Trident]: tridentReferenceNumberOptions,
  [Tenant.Fetch]: fetchReferenceNumberOptions,
  [Tenant.Syfan]: syfanReferenceNumberOptions,
  [Tenant.Tumalo]: tumaloReferenceNumberOptions,
  [Tenant.RLS]: fetchReferenceNumberOptions,
  [Tenant.Buchanan]: buchananReferenceNumberOptions,
  // Note: no tenant-specific reference number qualifier export available for Larkin yet.
  [Tenant.Larkin]: fetchReferenceNumberOptions,
  [Tenant.JRC]: jrcReferenceNumberOptions,
  // PT has no qualifier export yet: the shared list plus the qualifiers PT books that it lacks.
  [Tenant.PT]: [
    ...fetchReferenceNumberOptions,
    ...ptExtraReferenceNumberOptions,
  ],
};

export const tenantToOrderTypes: Record<Tenant, ValueLabelOption[]> = {
  [Tenant.Trident]: tridentOrderTypes,
  [Tenant.Fetch]: fetchOrderTypes,
  [Tenant.Syfan]: syfanOrderTypes,
  [Tenant.Tumalo]: tumaloOrderTypes,
  [Tenant.RLS]: rlsOrderTypes,
  [Tenant.Buchanan]: buchananOrderTypes,
  [Tenant.Larkin]: larkinOrderTypes,
  [Tenant.JRC]: jrcOrderTypes,
  [Tenant.PT]: ptOrderTypes,
};

/**
 * Gets transport type options based on a tenant's URL
 * @param tms - The TMS integration
 * @returns Array of transport type options for the tenant
 */
export const getTransportTypeOptions = (
  tms: Undef<IntegrationCore>
): ValueLabelOption[] => {
  const tenant = getTenantFromUrl(tms?.tenant || '');

  if (!tenant) {
    return [];
  }

  const opts = tenantToTransportTypeOptions[tenant];
  if (!opts || opts.length === 0) {
    captureException(
      `No transport type options configured for Mcleod tenant ${tms?.tenant}, please add them`,
      {
        tenant: tms?.tenant,
        tmsID: tms?.id,
      }
    );
  }

  return opts;
};

/**
 * Gets commodity options based on a tenant's URL
 * @param tms - The TMS integration
 * @returns Array of commodity options for the tenant
 */
export const getCommodityOptions = (
  tms: Undef<IntegrationCore>
): McleodCommodityOption[] => {
  const tenant = getTenantFromUrl(tms?.tenant || '');

  if (!tenant) {
    return [];
  }

  const opts = tenantToCommodityOptions[tenant];
  if (!opts || opts.length === 0) {
    captureException(
      `No commodity options configured for Mcleod tenant ${tms?.tenant}`,
      {
        tenant: tms?.tenant,
        tmsID: tms?.id,
      }
    );
  }

  return opts;
};

/**
 * Gets revenue codes based on a URL or tenant identifier
 * @param tms - The TMS integration
 * @returns Array of revenue codes for the tenant
 */
export const getRevenueCodes = (
  tms: Undef<IntegrationCore>
): ValueLabelOption[] => {
  const tenant = getTenantFromUrl(tms?.tenant || '');

  if (!tenant) {
    return [];
  }

  const opts = tenantToRevenueCodes[tenant];
  if (!opts || opts.length === 0) {
    captureException(
      `No revenue codes configured for Mcleod tenant ${tms?.tenant}, please add them`,
      {
        tenant: tms?.tenant,
        tmsID: tms?.id,
      }
    );
  }

  return opts;
};

/**
 * Gets reference number options based on a tenant's URL
 * @param tms - The TMS integration
 * @returns Array of reference number options for the tenant
 */
export const getReferenceNumberOptions = (
  tms: Undef<IntegrationCore>
): McleodReferenceNumberOption[] => {
  const tenant = getTenantFromUrl(tms?.tenant || '');

  if (!tenant) {
    return [];
  }

  const opts = tenantToReferenceNumberOptions[tenant];
  if (!opts || opts.length === 0) {
    captureException(
      `No reference number options configured for Mcleod tenant ${tms?.tenant}`,
      {
        tenant: tms?.tenant,
        tmsID: tms?.id,
      }
    );
  }

  return opts;
};

export const getOrderTypeOptions = (
  tms: Undef<IntegrationCore>
): ValueLabelOption[] => {
  const tenant = getTenantFromUrl(tms?.tenant || '');

  if (!tenant) {
    return [];
  }

  const opts = tenantToOrderTypes[tenant];
  if (!opts || opts.length === 0) {
    captureException(
      `No order type options configured for Mcleod tenant ${tms?.tenant}`,
      {
        tenant: tms?.tenant,
        tmsID: tms?.id,
      }
    );
  }

  return opts;
};

export const normalizeCollectionMethodValue = (
  value: MaybeUndef<string>,
  options: SelectOption[]
): string => {
  if (!value) {
    return '';
  }

  const normalizedValue = value.trim().toLowerCase();
  const matchedOption = options.find(
    (option) =>
      option.value.trim().toLowerCase() === normalizedValue ||
      option.label.trim().toLowerCase() === normalizedValue
  );

  return matchedOption?.label ?? value;
};

export const ensureCurrentValueOption = (
  options: SelectOption[],
  currentValue: MaybeUndef<string>
): SelectOption[] => {
  const trimmedValue = currentValue?.trim();
  if (!trimmedValue) {
    return options;
  }

  const hasMatch = options.some(
    (option) => option.value.trim().toLowerCase() === trimmedValue.toLowerCase()
  );

  if (hasMatch) {
    return options;
  }

  return [{ value: trimmedValue, label: trimmedValue }, ...options];
};

const normalizeMcleodOptionLabel = (label: string): string =>
  label.trim().toUpperCase();

const stripMcleodOptionCodePrefix = (label: string): string => {
  const normalizedLabel = normalizeMcleodOptionLabel(label);
  const delimiterIndex = normalizedLabel.indexOf(' - ');

  return delimiterIndex >= 0
    ? normalizedLabel.slice(delimiterIndex + 3)
    : normalizedLabel;
};

const matchesMcleodOptionLabel = (label: string, normalizedInput: string) => {
  const normalizedLabel = normalizeMcleodOptionLabel(label);

  return (
    normalizedLabel === normalizedInput ||
    stripMcleodOptionCodePrefix(label) === normalizedInput
  );
};

/**
 * Converts an order type label to its corresponding code value.
 * This is needed because the backend returns labels but expects codes when submitting.
 * @param orderTypeLabel - The order type label (e.g., "AVAILABLE TO COVER-HOT LOAD")
 * @param tms - The TMS integration object to determine tenant
 * @returns The order type code (e.g., "AVAIAHOT") or the original value if not found
 */
export const convertOrderTypeLabelToCode = (
  orderTypeLabel: string | null | undefined,
  tms: Undef<IntegrationCore>
): string | null | undefined => {
  if (!orderTypeLabel) {
    return orderTypeLabel;
  }

  const tenant = getTenantFromUrl(tms?.tenant || '');
  if (!tenant) {
    return orderTypeLabel;
  }

  const orderTypeOptions = tenantToOrderTypes[tenant];
  if (!orderTypeOptions || orderTypeOptions.length === 0) {
    return orderTypeLabel;
  }

  const matchingOption = orderTypeOptions.find((option) =>
    matchesMcleodOptionLabel(option.label, orderTypeLabel.toUpperCase())
  );

  return matchingOption ? matchingOption.value : orderTypeLabel;
};

/**
 * Converts mode value from backend format to Select component format.
 * Backend sends "TL" but Select expects "Truckload".
 * @param mode - The mode value from backend (e.g., "TL", "LTL")
 * @returns McLeod BE mode key for Select options (`TL` / `LTL`)
 */
export const convertModeToSelectValue = (
  mode: string | null | undefined
): string => {
  if (!mode) {
    return 'TL';
  }

  const lower = mode.toLowerCase();
  if (lower === 'tl' || lower === 'truckload') {
    return 'TL';
  }
  if (lower === 'ltl') {
    return 'LTL';
  }

  return mode;
};

/**
 * Converts order type value to Select component format.
 * Checks if value is already a code, otherwise converts label to code.
 * @param orderType - The order type value (could be label or code)
 * @param tms - The TMS integration object to determine tenant
 * @returns The order type code/value expected by Select component
 */
export const convertOrderTypeToSelectValue = (
  orderType: string | null | undefined,
  tms: Undef<IntegrationCore>
): string | null | undefined => {
  if (!orderType) {
    return orderType;
  }

  const orderTypeOptions = getOrderTypeOptions(tms);
  if (!orderTypeOptions || orderTypeOptions.length === 0) {
    return orderType;
  }

  return normalizeMcleodOrderType(orderType, orderTypeOptions) || orderType;
};

/**
 * Converts transport type value to Select component format.
 * Checks if value is already a code, otherwise converts label to code.
 * @param transportType - The transport type value (could be label or code)
 * @param tms - The TMS integration object to determine tenant
 * @returns The transport type code/value expected by Select component
 */
export const convertTransportTypeToSelectValue = (
  transportType: string | null | undefined,
  tms: Undef<IntegrationCore>
): string | null | undefined => {
  if (!transportType) {
    return transportType;
  }

  const transportTypeOptions = getTransportTypeOptions(tms);
  if (!transportTypeOptions || transportTypeOptions.length === 0) {
    return transportType;
  }

  return (
    normalizeMcleodTransportType(transportType, transportTypeOptions) ||
    transportType
  );
};

/**
 * Normalizes a transport type string to a valid McLeod transport type value code.
 * Handles AI suggestions and existing load data that may contain labels instead of value codes.
 *
 * @param transportType - The transport type string (could be a value code like 'V' or label like 'VAN')
 * @param transportTypeOptions - The available transport type options for the tenant
 * @returns The normalized value code (e.g., 'V' for Van), or empty string if no match
 */
export const normalizeMcleodTransportType = (
  transportType: Undef<string>,
  transportTypeOptions: ValueLabelOption[]
): string => {
  if (!transportType) return '';

  const normalizedInput = transportType.trim().toUpperCase();

  const transportTypeValueMatch = transportTypeOptions.find(
    (t) => t.value.toUpperCase() === normalizedInput
  );
  if (transportTypeValueMatch) return transportTypeValueMatch.value;

  const transportTypeLabelMatch = transportTypeOptions.find((t) =>
    matchesMcleodOptionLabel(t.label, normalizedInput)
  );
  if (transportTypeLabelMatch) return transportTypeLabelMatch.value;

  // Fallbacks for common variations
  if (normalizedInput.includes('VAN')) return 'V';
  if (normalizedInput.includes('REEFER')) return 'R';
  if (normalizedInput.includes('FLATBED')) return 'F';

  return '';
};

/**
 * Normalizes an order type string to a valid McLeod order type value code.
 * Handles BE responses that may contain labels instead of value codes.
 *
 * @param orderType - The order type string (could be a value code or label)
 * @param orderTypeOptions - The available order type options for the tenant
 * @returns The normalized value code, or empty string if no match
 */
export const normalizeMcleodOrderType = (
  orderType: Undef<string>,
  orderTypeOptions: ValueLabelOption[]
): string => {
  if (!orderType) return '';

  const normalizedInput = orderType.trim().toUpperCase();

  const valueMatch = orderTypeOptions.find(
    (o) => o.value.toUpperCase() === normalizedInput
  );
  if (valueMatch) return valueMatch.value;

  const labelMatch = orderTypeOptions.find((o) =>
    matchesMcleodOptionLabel(o.label, normalizedInput)
  );
  if (labelMatch) return labelMatch.value;

  return '';
};
